<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                :showAddNew="false"
                title="IPD Statement"
                :showExtraBtn="false"
            />
        </div>

        <div class="px-2">
            <DateQuerySetter
                btnTitle="Go"
                @onClickGo="onSearch"
                :isHideDate="true"
            >
                <div class="col-12 mb-1">
                    <div class="row">
                        <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                            <label for="colFormLabel" class="col-form-label">Registration status </label>
                            <v-select 
                                v-model="params.reg_status"
                                placeholder="Select registration status" class="w-100" 
                                :options="registrationStatus" label="name"
                                :reduce="(name) => name.value" 
                            />
                        </div>
                        <div v-show="params.reg_status !== 'admitted'" class="col-12 col-sm-6 col-md-4 col-lg-4">
                            <label 
                                for="colFormLabel" 
                                class="col-form-label col-xl-3"
                            >
                                From
                            </label>
                            <input
                                v-model="params.start_date"
                                type="date"
                                class="form-control date-mask" 
                                placeholder="From"
                            >
                        </div>
                        
                        <div v-show="params.reg_status !== 'admitted'" class="col-12 col-sm-6 col-md-4 col-lg-4">
                            <label 
                                for="colFormLabel"
                                class="col-form-label 'col-xl-3'"
                            >
                                To
                            </label>
                            <input
                                v-model="params.end_date"
                                type="date"
                                class="form-control date-mask" 
                                placeholder="From"
                            >   
                        </div>
                    </div>
                </div>

                <div class="col-12 col-sm-6 col-md-3 col-lg-3">
                    <label for="colFormLabel" class="col-form-label">Search</label>
                    <AsyncSelect
                        placeholder="Patient Name, ID, Mobile No"
                        v-model="params.patient"
                        :api-service="fetchContactProfiles"
                        :additional-query="{ type: 'patient'}"
                        :format-label="formatPatientLabel"
                    />
                </div>

                <div class="col-6 col-sm-3 col-md-3 col-lg-3">
                    <label for="colFormLabel" class="col-form-label">Admission no</label>
                    <AsyncSelect
                        :key="admissionComp"
                        placeholder="Admission no"
                        v-model="params.admission_no"
                        :api-service="fetchIpdRegNumbers"
                        :additional-query="{ contact_profile_id: contactId }"
                        :format-label="formatPatientLabel"
                    />
                </div>

                <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                    <label for="colFormLabel" class="col-form-label">Select Consultant </label>
                    <AsyncSelect
                        placeholder="Select consutant"
                        v-model="params.doctor"
                        :api-service="fetchServiceList"
                        :additional-query="{ resource_type: 'human_resources' }"
                        :format-label="formatPatientLabel"
                    />
                </div>

                <div class="col-12 col-sm-6 col-md-3 col-lg-3">
                    <label for="colFormLabel" class="col-form-label">Select Bed/Cabin </label>
                    <AsyncSelect
                        placeholder="Select Bed/Cabin"
                        v-model="params.place"
                        :api-service="fetchServiceList"
                        :additional-query="{ resource_type: 'places' }"
                        :format-label="formatPatientLabel"
                    />
                </div>

            </DateQuerySetter>
        </div>

        <div class="col-12 px-2 mt-2 mb-5">
            <ListTable 
                :tableItems="tableItems"
            />
        </div>

        <div class="px-2 position-absolute bottom-0">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

    </div>

</template>

<script setup>
    import TitleButton from '@/components/atom/TitleButton';
    import ListTable from '@/views/page/company/hospital/ipd-bill-list/IPDBillListTable.vue';
    import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import handleHospital from '@/services/modules/hospital'
    import Loader from '@/components/atom/LoaderComponent'
    import Pagination from '@/components/atom/Pagination'
    import { useStore } from 'vuex';
    import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
    import handleContact from "@/services/modules/contact";
    import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
    import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
    import { resetPaginationPage } from '@/services/utils/global';

    const $router = useRouter();
    const $route = useRoute();
    const $store = useStore();
    const showError = inject('showError');
    const showSuccess = inject('showSuccess');
    const loading = ref(false);
    const { fetchContactProfiles } = handleContact()
    const { formatPatientLabel } = useAsyncDropdownHelper()
    const { 
        fetchIPDRegistrationList, 
        fetchIpdRegStatus, 
        fetchServiceList, 
        fetchIpdRegNumbers 
    } = handleHospital()
    const params = reactive({
        start_date: $route.query.start,
        end_date: $route.query.end,
        status: null,
        offset: 20,
        patient: null,
        doctor: null,
        place: null,
        reg_status: null,
        admission_no: null,
    });
    const registrationStatus = reactive([])
    const tableItems = ref([]);
    const admissionComp = ref(null);

    const contactId = computed(() => params.patient ? params.patient.id : '')
    const isDateFilterable = computed(() => params.reg_status !== 'admitted')

    watch(contactId, (newVal, oldVal) => {
        admissionComp.value = newVal;
        params.admission_no = null;
    })

    //Functions
    const setPaginationData = (data) => {
        $store.commit('setPaginationData', data)
    };

    const onSearch = async () => {
        resetPaginationPage($store, $route, $router)
        await getRegistrationList(1);
    }

    const setPagination = (data) => {
        setPaginationData({
            records: data.total,
            perPage: data.per_page,
            currentPage: data.current_page,
        })
    }

    async function onPageChange(page) {
        let routeQuery = Object.assign({}, $route.query)
        routeQuery.page = page
        $router.push({path: $route.path, query: routeQuery})
        await getRegistrationList();
    }

    function getQuery(page = null) {
        let query = `?company_id=${$route.params.companyId}`;
        const pageNo = page ? page : $route.query.page
        if (pageNo) query += '&page=' + pageNo
        if (params.reg_status) query += '&reg_status=' + params.reg_status
        if (isDateFilterable.value && params.start_date) query += '&start_date=' + params.start_date
        if (isDateFilterable.value && params.end_date) query += '&end_date=' + params.end_date
        if (params.patient) query += '&patient_id=' + params.patient.id
        if (params.doctor) query += '&doctor_id=' + params.doctor.id
        if (params.place) query += '&place_id=' + params.place.id
        if (params.admission_no) query += '&admission_id=' + params.admission_no.id
        query += '&offset=' + params.offset
        return query;
    }

    const getRegistrationList = async (page = null) => {
        let query = getQuery(page);
        loading.value = true
        await fetchIPDRegistrationList(query).then(res => {
            loading.value = false
            if (!res.status){
                resetPaginationPage($store, $route, $router)
                tableItems.value = [];
                return;
            }
            tableItems.value = res.data.data;
            setPagination(res.data.meta)
        }).finally(() => {
            loading.value = false
        })
    }

    const getRegStatus = () => {
        let query = `?company_id=${$route.params.companyId}`;
        fetchIpdRegStatus(query).then(res=> {
            if(!res.data) return;
            registrationStatus.push(...res.data);
        })
    }

    onMounted( async() => {
        params.reg_status = 'admitted';
        await Promise.all([
            getRegistrationList(),
            getRegStatus(),
        ])
    });
</script>

<style>

</style>